import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isLoading: boolean;
  adminCheckError: string | null;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [adminCheckError, setAdminCheckError] = useState<string | null>(null);
  const lastCheckedUserId = useRef<string | null>(null);

  const withTimeout = async <T,>(
    promise: Promise<T>,
    ms: number,
    label: string
  ): Promise<T> => {
    let timeoutId: number | undefined;
    const timeoutPromise = new Promise<T>((_resolve, reject) => {
      timeoutId = window.setTimeout(() => {
        reject(new Error(`${label} timed out after ${ms}ms`));
      }, ms);
    });

    try {
      return await Promise.race([promise, timeoutPromise]);
    } finally {
      if (timeoutId) window.clearTimeout(timeoutId);
    }
  };

  const isCiWebGroupEmail = (email?: string | null) => {
    const e = (email ?? "").toLowerCase().trim();
    return e.endsWith("@ciwebgroup.com");
  };

  const checkAdminRole = async (userId: string) => {
    try {
      const { data, error } = await supabase.rpc('has_role', {
        _user_id: userId,
        _role: 'admin',
      });

      if (error) {
        console.error('Error checking admin role (rpc has_role):', error);
        return false;
      }

      return !!data;
    } catch (err) {
      console.error('Error in checkAdminRole:', err);
      return false;
    }
  };

  const ensureCiWebGroupAdmin = async (email?: string | null) => {
    if (!isCiWebGroupEmail(email)) return;

    try {
      const { error } = await supabase.rpc("ensure_ciwebgroup_admin");
      if (error) {
        console.error("Error ensuring CI Web Group admin role:", error);
      }
    } catch (err) {
      console.error("Error in ensureCiWebGroupAdmin:", err);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const runAdminCheck = async (sessionUser: User) => {
      setAdminCheckError(null);
      try {
        await withTimeout(ensureCiWebGroupAdmin(sessionUser.email), 8000, 'ensure admin role');
        const adminStatus = await withTimeout(checkAdminRole(sessionUser.id), 8000, 'check admin role');
        if (isMounted) setIsAdmin(adminStatus);
      } catch (err) {
        if (!isMounted) return;
        console.error('Admin permission check failed:', err);
        setIsAdmin(false);
        setAdminCheckError(
          err instanceof Error
            ? err.message
            : 'Unable to verify admin permissions. Please try again.'
        );
      }
    };

    // Set up auth state listener — does NOT control isLoading
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!isMounted) return;

      setSession(nextSession);
      setUser(nextSession?.user ?? null);

      if (nextSession?.user) {
        // Only re-check admin if the user ID actually changed (not on token refresh)
        if (nextSession.user.id !== lastCheckedUserId.current) {
          lastCheckedUserId.current = nextSession.user.id;
          setTimeout(() => {
            if (isMounted) runAdminCheck(nextSession.user);
          }, 0);
        }
      } else {
        setIsAdmin(false);
        setAdminCheckError(null);
        lastCheckedUserId.current = null;
      }
    });

    // Initial load — this is the ONLY place that controls isLoading
    const initializeAuth = async () => {
      try {
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        if (!isMounted) return;

        setSession(initialSession);
        setUser(initialSession?.user ?? null);

        if (initialSession?.user) {
          lastCheckedUserId.current = initialSession.user.id;
          await runAdminCheck(initialSession.user);
        } else {
          setIsAdmin(false);
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Auth initialization failed:', err);
        setIsAdmin(false);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string) => {
    if (!email.toLowerCase().endsWith('@ciwebgroup.com')) {
      return {
        error: new Error('Registration is restricted to CI Web Group team members only.')
      };
    }

    const redirectUrl = `${window.location.origin}/`;

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl
        }
      });

      if (error) {
        return { error: error as Error | null };
      }

      if (data?.user && data.user.identities?.length === 0) {
        return {
          error: new Error('An account with this email already exists. Please sign in instead.')
        };
      }

      return { error: null };
    } catch (err) {
      console.error('SignUp error:', err);
      if (err instanceof TypeError && err.message === 'Failed to fetch') {
        return {
          error: new Error('Network error: Unable to connect. Please check your internet connection and try again.')
        };
      }
      return { error: err as Error };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsAdmin(false);
    lastCheckedUserId.current = null;
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/backoffice/reset-password`,
      });
      return { error: error as Error | null };
    } catch (err) {
      console.error('Reset password error:', err);
      if (err instanceof TypeError && err.message === 'Failed to fetch') {
        return {
          error: new Error('Network error: Unable to connect. Please check your internet connection.')
        };
      }
      return { error: err as Error };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isAdmin,
        isLoading,
        adminCheckError,
        signIn,
        signUp,
        signOut,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
